// ABOUTME: Tracks whether the user has edited a react-hook-form form and gates closing on it: a close
// ABOUTME: with edits opens the "leave without saving?" prompt; "don't save" restores the defaults.

import { useEffect, useState } from "react"
import { holdUnsavedWork } from "@shared/utils/unsavedWork"
import type { DeepPartial, DefaultValues, FieldValues, UseFormReset, UseFormWatch } from "react-hook-form"

interface Options<T extends FieldValues> {
  watch: UseFormWatch<T>
  reset: UseFormReset<T>
  /** formState.defaultValues: what "don't save" puts back. NoInfer: the form type comes from watch/reset. */
  defaultValues: Readonly<DeepPartial<NoInfer<T>>> | undefined
  onClose: () => void
  /**
   * Work the form does not hold, asked about on a close too — a message being written in the window's discussion.
   * Read at the close: the prompt shows while it holds. "Don't save" drops the form's edits only: the message lives on
   * until it is sent or dropped by its own question (model/discussion/messageDrafts).
   */
  hasOtherWork?: () => boolean
}

export interface UnsavedChangesGuard<T extends FieldValues = FieldValues> {
  /** The user has edited a field since the form opened. */
  isDirty: boolean
  /** The "leave without saving?" prompt is showing. */
  isLeaveOpen: boolean
  /** Close, or ask first when there are edits. */
  requestClose: () => void
  /** Dismiss the prompt and keep editing. */
  stayOpen: () => void
  /** Drop the edits, restore the defaults and close. */
  discardAndClose: () => void
  /** Forget the edits after a save that keeps the form open. */
  markClean: () => void
  /**
   * A save went through, but the window stays to ask about work the form does not hold — a message whose attachment
   * still uploads: what was saved is the form's start now, its edits clean, and the prompt opens. A method, not a
   * property: its parameter is then checked both ways, so any form's guard goes where the dialogs take a guard.
   */
  askAfterSave(saved: T): void
}

export const useUnsavedChangesGuard = <T extends FieldValues>(options: Options<T>): UnsavedChangesGuard<T> => {
  const { watch, reset, defaultValues, onClose, hasOtherWork } = options
  const [isDirty, setIsDirty] = useState(false)
  const [isLeaveOpen, setIsLeaveOpen] = useState(false)

  useEffect(() => {
    if (isDirty) return
    // watch(callback) forwards every state notification that carries values
    // (react-hook-form 7.72 index.esm.mjs:2095-2099). Only a field's onChange
    // adds the event type (1923-1927); setValue publishes none (1869-1879), so
    // a value filled in by code does not count as an edit.
    const subscription = watch((_value, { name, type }) => {
      if (!name || !type) return
      setIsDirty(true)
    })
    return () => subscription.unsubscribe()
  }, [watch, isDirty])

  // Told to whoever might throw the tab away: the PWA updater applies an update
  // silently when the app comes back from a long background, and it has no way
  // to see a form from out there.
  useEffect(() => {
    if (!isDirty) return
    return holdUnsavedWork()
  }, [isDirty])

  return {
    isDirty,
    isLeaveOpen,
    requestClose: () => {
      if (isDirty || hasOtherWork?.()) {
        setIsLeaveOpen(true)
        return
      }
      onClose()
    },
    stayOpen: () => setIsLeaveOpen(false),
    discardAndClose: () => {
      setIsDirty(false)
      setIsLeaveOpen(false)
      reset({ ...defaultValues } as DefaultValues<T>)
      onClose()
    },
    markClean: () => setIsDirty(false),
    askAfterSave: (saved) => {
      // The fields keep what they hold; only their start moves. keepDirtyValues off: a form that keeps it on its
      // resets (WorkoutUpdate's resetOptions, merged into every reset — react-hook-form index.esm.mjs:2479-2481) would
      // keep the saved edits dirty (:2457-2467), and its next save would send them again.
      reset(saved, { keepValues: true, keepDirtyValues: false })
      setIsDirty(false)
      setIsLeaveOpen(true)
    },
  }
}
